<ng-container
  *ngIf="
    (institutionIsOnFreeTier$ | async) || (isAutomaticBillingWithoutSubscription$ | async);
    else subscriptionDetailsTemplate
  "
>
  <main class="usage-main">
    <div class="content-container">
      <div class="plan-details-container">
        <div class="plan-header">
          <!-- AUTOMATIC BILLING CASES -->
          <ng-container
            *ngIf="isAutomaticBillingWithoutSubscription$ | async; else manualBillingCase"
          >
            <!-- TRIAL ENDING SOON -->
            <ng-container
              *ngIf="
                institution.billingStatus?.forceBillDate | dateIsNotPast;
                else automaticUpgradeCase
              "
            >
              <ng-container *ngTemplateOutlet="trialEndingTemplate"></ng-container>
            </ng-container>

            <!-- UPGRADE TO PAID PLAN (Default for automatic billing) -->
            <ng-template #automaticUpgradeCase>
              <ng-container *ngTemplateOutlet="upgradeSubscriptionTemplate"></ng-container>
            </ng-template>
          </ng-container>

          <!-- MANUAL BILLING CASE -->
          <ng-template #manualBillingCase>
            <ng-container *ngTemplateOutlet="manualFreeTierTemplate"></ng-container>
          </ng-template>
        </div>
      </div>
    </div>
  </main>
</ng-container>

<ng-template #subscriptionDetailsTemplate>
  <ng-container *ngIf="isLoading$ | async">
    <div class="pencil-skeleton-loading-container">
      <div *ngFor="let i of skeletonBars" class="skeleton-bar"></div>
    </div>
  </ng-container>
  <ng-container *ngIf="billingData$ | async as billingData">
    <main class="usage-main">
      <div class="content-container">
        <div class="plan-details-container">
          <!-- First part -->
          <div class="plan-header">
            <p class="plan-name">
              {{ 'Plan:' | translate }} {{ billingData.customerPlan.planName }}
            </p>
            <p class="next-billing-indicator">
              <ng-container *ngIf="isTrialInstitution$ | async; else nonTrialInstitutionTemplate">
                {{ billingData.billingReport.nextBillingDate | trialEndsTranslation }}
              </ng-container>

              <ng-template #nonTrialInstitutionTemplate>
                <ng-container
                  *ngIf="billingData.billingReport.nextBillingDate; else noNextBillingDateTemplate"
                >
                  {{ billingData.billingReport.nextBillingDate | nextPaymentTranslation }}
                </ng-container>
                <ng-template #noNextBillingDateTemplate>
                  {{ 'Next payment' | translate }}
                  {{ 'not available. Contact Support to upgrade your plan' | translate }}
                </ng-template>
              </ng-template>
            </p>
            <app-pencil-button
              *ngIf="institution.paywallConfig?.billingType === BillingType.Automatic"
              class="cmt-16"
              theme="secondary-outline"
              data-name="plan-and-usage-open-chargebee-portal-btn"
              icon="edit"
              [iconConfig]="{ type: IconTypes.matIcon }"
              [label]="'Manage' | translate"
              (clickEvent)="createPortalSession()"
            >
            </app-pencil-button>
            <ng-container *ngIf="institution.paywallConfig?.billingType === BillingType.Manual">
              <ng-container *ngTemplateOutlet="contactSupportButton"></ng-container>
            </ng-container>
          </div>

          <!-- Second part -->
          <div class="plan-details" *ngIf="!(isTrialInstitution$ | async)">
            <p class="details-header">{{ 'PLAN DETAILS' | translate }}</p>
            <div class="detail-item">
              <p class="detail-title">{{ 'Billing Frequency' | translate }}</p>
              <p class="detail-value">
                {{ billingFrequencyLabels[billingData.customerPlan.billingFrequency] }}
              </p>
            </div>
            <div class="detail-item">
              <p class="detail-title">{{ 'Next Billing Date' | translate }}</p>
              <p class="detail-value">
                {{ billingData.billingReport.nextBillingDate | dateLocale: 'longDate2' }}
              </p>
            </div>
          </div>
        </div>
        <ng-container
          *ngIf="billingData.stepBasedBillingSummary as summary; else v1BillingSummaryTemplate"
        >
          <div class="step-based-billing-summary">
            <h3 class="step-based-billing-header">{{ 'Billing Summary' | translate }}</h3>

            <div class="step-based-billing-box">
              <div class="step-based-billing-banner">
                <app-pencil-icon [type]="IconTypes.svgIcon" name="info-dark"></app-pencil-icon>
                <!-- prettier-ignore -->
                <span>
                  {{ 'Your billing period runs from {{startDate}} to {{endDate}}' | translate: {
                    startDate: (billingData.billingReport.billingPeriodStartDate | dateLocale: 'mini'),
                    endDate: (billingData.billingReport.billingPeriodEndDate | dateLocale: 'longDate')
                  } }}
                </span>
              </div>
              <div class="step-based-balance-due-section">
                <p class="step-based-balance-due-title">{{ 'Balance Due' | translate }}</p>
                <p class="step-based-balance-due-amount">
                  {{ summary.balanceDue | currency: 'USD':'symbol-narrow' }}
                </p>
                <!-- prettier-ignore -->
                <p class="step-based-balance-due-subtext">
                  {{ 'Final amount calculated on {{date}}. Not inclusive of discounts and promotions' | translate: {
                    date: ((billingData.billingReport.nextBillingDate || billingData.billingReport.billingPeriodEndDate) | dateLocale: 'longDate')
                  } }}
                </p>
              </div>

              <div class="step-based-billing-table">
                <div class="step-based-table-header">
                  <div class="step-based-col-desc">{{ 'DESCRIPTION' | translate }}</div>
                  <div class="step-based-col-qty">{{ 'QUANTITY' | translate }}</div>
                  <div class="step-based-col-price">{{ 'UNIT PRICE' | translate }}</div>
                  <div class="step-based-col-amt">{{ 'AMOUNT' | translate }}</div>
                </div>

                <div class="step-based-table-row" *ngFor="let row of summary.lineItems">
                  <div class="step-based-col-desc">
                    {{ row.description }}
                    <app-pencil-icon
                      *ngIf="row.infoTooltip"
                      [type]="IconTypes.svgIcon"
                      name="info-outline-dark-gray"
                      class="info-icon"
                      [appTooltip]="row.infoTooltip"
                      [appTooltipPlacement]="'top-center'"
                      [appTooltipArrow]="'bottom'"
                      [appTooltipCreateNewLine]="true"
                    ></app-pencil-icon>
                  </div>
                  <div class="step-based-col-qty">
                    <!-- prettier-ignore -->
                    {{ '{{hours}} hours' | translate: { hours: (row.quantity | number: '1.0-2') } }}
                  </div>
                  <div class="step-based-col-price">
                    <ng-container *ngIf="row.unitPrice !== undefined; else noPrice">
                      <!-- prettier-ignore -->
                      {{ '{{cost}} /hour' | translate: { cost: (row.unitPrice | currency:
                      'USD':'symbol-narrow') } }}
                    </ng-container>
                    <ng-template #noPrice>-</ng-template>
                  </div>
                  <div class="step-based-col-amt">
                    {{ row.amount | currency: 'USD':'symbol-narrow' }}
                  </div>
                </div>

                <div class="step-based-table-footer-row">
                  <div class="step-based-col-footer-desc">{{ 'Subtotal' | translate }}</div>
                  <div class="step-based-col-footer-amt">
                    {{ summary.subtotal | currency: 'USD':'symbol-narrow' }}
                  </div>
                </div>

                <div
                  class="step-based-table-footer-row"
                  *ngIf="summary.monthlyCreditDeduction as monthlyCreditDeduction"
                >
                  <div class="step-based-col-footer-desc">
                    {{ 'Monthly credit deduction' | translate }}
                    <app-pencil-icon
                      [type]="IconTypes.svgIcon"
                      name="info-outline-dark-gray"
                      class="info-icon"
                      [appTooltip]="monthlyCreditDeduction.tooltip"
                      [appTooltipPlacement]="'top-center'"
                      [appTooltipArrow]="'bottom'"
                    ></app-pencil-icon>
                  </div>
                  <div class="step-based-col-footer-amt">
                    -{{ monthlyCreditDeduction.amount | currency: 'USD':'symbol-narrow' }}
                  </div>
                </div>

                <div class="step-based-table-footer-row step-based-total-row">
                  <div class="step-based-col-footer-desc">{{ 'Balance due' | translate }}</div>
                  <div class="step-based-col-footer-amt">
                    {{ summary.balanceDue | currency: 'USD':'symbol-narrow' }}
                  </div>
                </div>
              </div>
            </div>

            <ng-container *ngTemplateOutlet="billingUsageHelpLink"></ng-container>
          </div>
        </ng-container>

        <ng-template #v1BillingSummaryTemplate>
          <div class="usage-details" *ngIf="!isSeatBased">
            <div class="billing-period">
              <p class="header">{{ 'Current Billing Period' | translate }}</p>
              <p class="interval">
                {{
                  ('Usage between ' | translate) +
                    (billingData.billingReport.billingPeriodStartDate | dateLocale: 'longDate2') +
                    (' - Present' | translate)
                }}
              </p>
            </div>
            <div class="billable-categories">
              <div
                *ngFor="let bItem of billingData.billingReport.billableItems"
                class="billable-category"
              >
                <p class="header">
                  {{ billableCategoriesDetails[bItem.feature].header | translate }}
                </p>
                <ng-container *ngIf="bItem.totalAvailableCredits; else noCredits">
                  <ng-container
                    *ngIf="{
                      usedCreditsRatio: bItem.totalUsage / bItem.totalAvailableCredits,
                      isUsageLTCredits: bItem.totalUsage < bItem.totalAvailableCredits
                    } as calc"
                  >
                    <p class="usage-details">
                      <!-- common part  -->
                      {{ bItem.totalUsage | number: '1.0-1' }}
                      {{ ' of ' + bItem.totalAvailableCredits }}
                      {{ billableCategoriesDetails[bItem.feature].unitDescText
                      }}<ng-container *ngIf="!calc.isUsageLTCredits && bItem.billableRate"
                        >{{
                          ('. Overages charged at ' | translate) +
                            (bItem.billableRate | currency: 'USD':'symbol-narrow') +
                            (' / user / hour' | translate)
                        }}
                      </ng-container>
                      <ng-container *ngIf="calc.isUsageLTCredits">
                        {{ ' (' + (calc.usedCreditsRatio | percent: '1.0-1') + ')' }}
                      </ng-container>
                    </p>
                    <div class="indicator-container">
                      <div
                        class="indicator-fill"
                        [ngClass]="calc.isUsageLTCredits ? 'credits-available' : 'credits-consumed'"
                        [ngStyle]="{
                          width: (calc.isUsageLTCredits ? calc.usedCreditsRatio * 100 : 100) + '%'
                        }"
                      ></div>
                    </div>
                  </ng-container>
                </ng-container>
                <ng-template #noCredits>
                  <p class="usage-details">
                    {{ bItem.totalUsage | number: '1.0-1' }}
                    {{ billableCategoriesDetails[bItem.feature].unitDescText }}
                    {{ ' since start of billing period.' | translate }}
                    <ng-container *ngIf="bItem.billableRate">
                      {{
                        (' Charged at ' | translate) +
                          (bItem.billableRate | currency: 'USD':'symbol-narrow') +
                          (' / user / hour' | translate)
                      }}
                    </ng-container>
                  </p>
                </ng-template>
              </div>
            </div>
            <ng-container *ngTemplateOutlet="billingUsageHelpLink"></ng-container>
          </div>
        </ng-template>
      </div>
    </main>
  </ng-container>
</ng-template>

<ng-template #billingUsageHelpLink>
  <a
    class="billing-usage-help-link"
    href="https://helpdesk.pencilspaces.com/en/articles/12163955-how-is-my-billing-usage-calculated"
    target="_blank"
    rel="noopener noreferrer"
  >
    {{ 'How is my billing usage calculated?' | translate }}
  </a>
</ng-template>

<ng-template #contactSupportButton>
  <app-pencil-button
    class="cmt-16"
    theme="secondary-outline"
    [icon]="'help_outline'"
    [iconConfig]="{ type: IconTypes.matIcon }"
    [label]="'Contact Support' | translate"
    (clickEvent)="openIntercom()"
  ></app-pencil-button>
</ng-template>

<ng-template #viewPlansButton>
  <app-pencil-button
    class="cmt-16"
    theme="secondary-outline"
    [icon]="'credit_card'"
    [iconConfig]="{ type: IconTypes.matIcon }"
    [label]="'View Plans' | translate"
    (clickEvent)="onViewPlans()"
  ></app-pencil-button>
</ng-template>

<!-- MESSAGE TEMPLATES -->
<ng-template #trialEndingTemplate>
  <p class="plan-name">{{ 'Plan: Trial' | translate }}</p>
  <p class="next-billing-indicator">
    {{ institution.billingStatus?.forceBillDate | trialEndsTranslation }},
    {{ "You'll need to upgrade to one of our plans to use our premium features" | translate }}
  </p>
  <ng-container
    *ngTemplateOutlet="hidePaymentPlans ? contactSupportButton : viewPlansButton"
  ></ng-container>
</ng-template>

<ng-template #upgradeSubscriptionTemplate>
  <p class="plan-name">{{ 'Plan: Free Tier' | translate }}</p>
  <p class="next-billing-indicator">
    {{ "You'll need to upgrade to one of our plans to use our premium features." | translate }}
  </p>
  <ng-container
    *ngTemplateOutlet="hidePaymentPlans ? contactSupportButton : viewPlansButton"
  ></ng-container>
</ng-template>

<ng-template #manualFreeTierTemplate>
  <p class="plan-name">{{ 'Plan: Free Tier' | translate }}</p>
  <p class="next-billing-indicator">
    {{ 'Contact Support to upgrade your plan' | translate }}
  </p>
  <ng-container *ngTemplateOutlet="contactSupportButton"></ng-container>
</ng-template>
